/**
 * Sync engine cliente.
 *
 * Drena la cola de pending changes (responses + complete) y la cola
 * de fotos pendientes contra el server cuando hay conectividad. Si una
 * sync falla por red, marca el change como pending de nuevo con
 * attempts++ y reintenta en la proxima oportunidad.
 *
 * Estrategia de scheduling:
 *   - listener 'online' del navegador
 *   - polling cada 30s si online
 *   - manual via syncNow()
 *
 * Conflict resolution: si server retorna status='conflict' (ej.
 * assessment_locked porque otro device ya cerro), borramos el change
 * de la cola y notificamos al UI (Sprint 4 muestra warning). Sprint
 * posterior puede levantar UI mas elaborada de resolucion manual.
 */
import { type CompleteResult, photosApi } from '../api/assessments.js';
import { apiRequest } from '../api/client.js';
import { getCryptoKey } from '../auth/crypto-key.js';
import { decryptJson } from './crypto.js';
import { offlineDB } from './db.js';
import {
  listPending,
  listPendingPhotos,
  loadPhotoBlob,
  markChangeApplied,
  markChangeError,
  markChangeSyncing,
  markPhotoError,
  markPhotoUploaded,
} from './repository.js';

interface PushResultItem {
  client_change_id: string;
  status: 'applied' | 'already_applied' | 'conflict' | 'error';
  error?: string;
}

interface PushResponse {
  results: PushResultItem[];
  server_ts: string;
}

let inFlight = false;
let pollTimer: ReturnType<typeof setInterval> | null = null;
const listeners = new Set<(state: SyncState) => void>();

export interface SyncState {
  online: boolean;
  syncing: boolean;
  pendingChanges: number;
  pendingPhotos: number;
  lastSyncedAt: number | null;
  lastError: string | null;
}

let lastState: SyncState = {
  online: typeof navigator === 'undefined' ? true : navigator.onLine,
  syncing: false,
  pendingChanges: 0,
  pendingPhotos: 0,
  lastSyncedAt: null,
  lastError: null,
};

function emit(): void {
  for (const cb of listeners) cb(lastState);
}

export function subscribeSyncState(cb: (s: SyncState) => void): () => void {
  listeners.add(cb);
  cb(lastState);
  return () => listeners.delete(cb);
}

async function refreshCounts(): Promise<void> {
  if (!offlineDB) return;
  const [changes, photos] = await Promise.all([
    offlineDB.pending.where('status').equals('pending').count(),
    offlineDB.photos.where('status').equals('pending').count(),
  ]);
  lastState = { ...lastState, pendingChanges: changes, pendingPhotos: photos };
  emit();
}

async function pushChanges(): Promise<void> {
  const key = getCryptoKey();
  if (!key) return;
  const pending = await listPending();
  if (pending.length === 0) return;

  // Descifrar cada change para reconstruir el payload del POST.
  const changes: unknown[] = [];
  const idMap = new Map<string, string>(); // clientChangeId → kind
  for (const p of pending) {
    try {
      const payload = await decryptJson<Record<string, unknown>>(key, {
        iv: p.iv,
        ciphertext: p.ciphertext,
      });
      if (p.kind === 'responses') {
        changes.push({
          client_change_id: p.clientChangeId,
          kind: 'responses',
          assessment_id: p.assessmentId,
          instance_id: payload.instance_id,
          responses: payload.responses,
          ...(payload.auditor_notes !== undefined ? { auditor_notes: payload.auditor_notes } : {}),
          ts: new Date(p.ts).toISOString(),
        });
      } else if (p.kind === 'complete') {
        changes.push({
          client_change_id: p.clientChangeId,
          kind: 'complete',
          assessment_id: p.assessmentId,
          ts: new Date(p.ts).toISOString(),
        });
      }
      idMap.set(p.clientChangeId, p.kind);
      await markChangeSyncing(p.clientChangeId);
    } catch (err) {
      await markChangeError(
        p.clientChangeId,
        err instanceof Error ? err.message : 'descifrado_fallo',
      );
    }
  }
  if (changes.length === 0) return;

  try {
    const res = await apiRequest<PushResponse>('/api/v1/sync/push', {
      method: 'POST',
      body: { changes },
    });
    for (const r of res.results) {
      if (r.status === 'applied' || r.status === 'already_applied' || r.status === 'conflict') {
        await markChangeApplied(r.client_change_id);
      } else {
        await markChangeError(r.client_change_id, r.error ?? 'unknown');
      }
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'network_error';
    for (const id of idMap.keys()) {
      await markChangeError(id, msg);
    }
    throw err;
  }
}

async function pushPhotos(): Promise<void> {
  const pending = await listPendingPhotos();
  for (const item of pending) {
    try {
      const blob = await loadPhotoBlob(item.id);
      if (!blob) {
        await markPhotoError(item.id, 'blob_perdido');
        continue;
      }
      // Reconstruir File desde Blob para FormData.
      const file = new File([blob], `${item.id}.bin`, { type: item.contentType });
      const meta = await photosApi.upload({
        assessmentId: item.assessmentId,
        instanceId: item.instanceId,
        questionRef: item.questionRef,
        file,
        capturedAt: new Date(item.capturedAt),
      });
      await markPhotoUploaded(item.id, meta.id);
    } catch (err) {
      await markPhotoError(item.id, err instanceof Error ? err.message : 'upload_error');
    }
  }
}

export async function syncNow(): Promise<{ ok: boolean; error?: string }> {
  if (inFlight) return { ok: true };
  if (!offlineDB || !getCryptoKey()) return { ok: false, error: 'no_offline_or_key' };
  inFlight = true;
  lastState = { ...lastState, syncing: true, lastError: null };
  emit();
  try {
    await pushPhotos();
    await pushChanges();
    lastState = {
      ...lastState,
      syncing: false,
      lastSyncedAt: Date.now(),
      lastError: null,
    };
    await refreshCounts();
    return { ok: true };
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'sync_error';
    lastState = { ...lastState, syncing: false, lastError: msg };
    emit();
    await refreshCounts();
    return { ok: false, error: msg };
  } finally {
    inFlight = false;
  }
}

export function startSyncScheduler(): void {
  if (typeof window === 'undefined') return;
  // Refresh inicial de contadores
  void refreshCounts();

  function handleOnline() {
    lastState = { ...lastState, online: true };
    emit();
    void syncNow();
  }
  function handleOffline() {
    lastState = { ...lastState, online: false };
    emit();
  }
  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  if (pollTimer) clearInterval(pollTimer);
  pollTimer = setInterval(() => {
    if (navigator.onLine && getCryptoKey()) {
      void syncNow();
    }
  }, 30_000);
}

export function stopSyncScheduler(): void {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

// Re-export para tipos del UI
export type { CompleteResult };
